import { BaseSyntheticEvent, forwardRef, InputHTMLAttributes, RefAttributes, useCallback, useState } from "react";
import {
  borderClassName,
  widthClassName,
  roundedClassName,
  inputClassName,
  inputColorClassName,
  sizeClassName,
  textAlignClassName,
  getFocusOutline,
} from "./commonClassNames";
import { BorderProps, WidthProp, InputColorProps, TextAlignProp } from "./commonProps";

type CurrentValue = number | "" | "-" | "+";

export type InputNumberFormatter = (value: unknown) => string;

export type InputNumberProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "className" | "onChange" | "onFocus" | "type"
> &
  RefAttributes<HTMLInputElement> &
  BorderProps &
  WidthProp &
  InputColorProps &
  TextAlignProp & {
    setValue: (value: number | null) => void;
    formatValue?: InputNumberFormatter;
  } & {
    leftIcon?: string;
    rightIcon?: string;
    className?: string;
    small?: boolean;
  };

export const InputNumber: React.FC<InputNumberProps> = forwardRef(
  (
    {
      transparent,
      neutral,
      textAlign,
      setValue,
      border,
      value,
      grouped,
      min,
      max,
      width,
      onBlur,
      leftIcon,
      rightIcon,
      placeholder,
      className,
      formatValue = (value: unknown) => String(value),
      id,
      small,
      ...props
    },
    ref,
  ) => {
    const [currentValue, setCurrentValue] = useState<CurrentValue>("");
    const [hasFocus, setHasFocus] = useState(false);

    const containerClassName = [
      "relative w-full h-fit",
      widthClassName({ width }),
      // borderClassName({ border, grouped }),
      // roundedClassName({ grouped }),
      className ?? "",
    ].join(" ");

    const inputCommonClassName = [
      "font-normal",
      small === true ? "h-8" : "",
      "w-full min-w-24",
      leftIcon ? (small ? "pl-6" : "pl-9") : "",
      rightIcon ? (small ? "pr-6" : "pr-9") : "",
      inputClassName(),
      inputColorClassName({ neutral, transparent }),
      roundedClassName({ grouped }),
      borderClassName({ border, grouped }),
      sizeClassName({ size: small ? "small" : undefined }),
      textAlignClassName({ textAlign }),
    ].join(" ");

    const displayClassName = [
      inputCommonClassName,
      "absolute top-0 left-0",
      "pointer-events-none",
      hasFocus ? "opacity-0" : "opacity-1",
    ].join(" ");

    const inputNumberClassName = [
      inputCommonClassName,
      hasFocus ? "opacity-1" : "opacity-0",
      getFocusOutline({ border }),
    ].join(" ");

    const coerceValue = useCallback((value: unknown) => {
      if (typeof value === "undefined") return "";
      if (value === "") return "";
      if (value === "--") return "-";
      if (value === "++") return "+";

      const num = Number(value);
      if (isNaN(num)) return "";

      return num;
    }, []);

    const handleOnBlur = (event: React.FocusEvent<HTMLInputElement>) => {
      let num = coerceValue(currentValue);

      if (typeof min === "number" && num < min) {
        num = min;
      }
      if (typeof max === "number" && num > max) {
        num = max;
      }

      if (typeof num === "number" && !isNaN(num)) {
        setValue(num);
      } else {
        setValue(null);
      }
      setHasFocus(false);
      onBlur?.(event);
    };

    const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = coerceValue(event.target.value);
      setCurrentValue(value);
    };

    const onFocus = () => {
      setCurrentValue(coerceValue(value));
      setHasFocus(true);
    };

    const ignoreEvent = useCallback((event: BaseSyntheticEvent) => {
      event.stopPropagation();
    }, []);

    const displayValue = (() => {
      if (hasFocus) return "";
      if (typeof value === "undefined") return "";
      return formatValue(value);
    })();

    const leftIconClassName = [
      "opacity-1",
      "z-10",
      "absolute",
      small ? "left-0 top-[2px]" : "left-1 top-[5px]",
      "pointer-events-none",
      "w-5",
      "text-center",
      sizeClassName({ size: small ? "small" : undefined }),
    ].join(" ");

    const rightIconClassName = [
      "opacity-1",
      "z-10",
      "absolute",
      small ? "right-0 top-[2px]" : "right-4 top-[5px]",
      "pointer-events-none",
      "w-5",
      "text-center",
      sizeClassName({ size: small ? "small" : undefined }),
    ].join(" ");

    return (
      <div className={containerClassName}>
        {leftIcon ? <div className={leftIconClassName}>{leftIcon}</div> : null}
        {rightIcon ? <div className={rightIconClassName}>{rightIcon}</div> : null}
        <input
          type="number"
          className={inputNumberClassName}
          min={min}
          max={max}
          onBlur={handleOnBlur}
          onChange={onChange}
          onFocus={onFocus}
          value={currentValue}
          placeholder={placeholder}
          {...props}
          ref={ref}
          id={`${id}Value`}
        />
        {/*
          The actual input number is behind an input text,
          in order to show a formatted value.

          Notice that using a `defaultValue` prop in the input text
          component could cause a missing re-render issue.
          It is used a `value` prop with a dummy `onChange` prop.
       */}
        <input
          aria-hidden="true"
          type="text"
          className={displayClassName}
          onChange={ignoreEvent}
          tabIndex={-1}
          value={displayValue}
          placeholder={placeholder}
          id={`${id}FormattedValue`}
        />
      </div>
    );
  },
);
